import type { GraphState, Node, Edge } from "./types";

/**
 * Initial cold-state seed for the Web. Pre-Act-0 layout:
 *
 *   - Aurora is a small hub on the Brazilian side (verified, with edges
 *     to her certifiers and prior anonymised trades — represented here
 *     by a thin Aurora-CDIAL-MAPA-ABCC tri-edge.
 *   - Al Madar is a single dim cold dot near the AE pole.
 *   - Pampagro and Gulf Phosphate sit dormant in the periphery (cold,
 *     foreshadowing T3).
 *   - Attestor placeholders (CDIAL, FAMBRAS, ABCC, MAPA, SGS, FUC, MOCCAE)
 *     are visible but greyed.
 *   - GLEIF cluster sits centre-back, ready to ignite at OrbID mint.
 *
 * The (x, y) coordinates are chosen so the BR cluster sits on the start
 * side and the AE cluster on the end side. RTL flipping is handled at the
 * render layer, not here — the simulation runs in canonical space.
 */

// Canonical poles, normalised to roughly [-1, 1]. The renderer maps to canvas px.
const BR_POLE = { x: -0.62, y:  0.00 };
const AE_POLE = { x:  0.62, y:  0.00 };

function mkNode(p: Partial<Node> & Pick<Node, "id" | "kind" | "state" | "accent" | "label">): Node {
  return {
    initials: undefined,
    flag: undefined,
    bankName: undefined,
    fixed: undefined,
    pulseUntil: 0,
    x: 0, y: 0, vx: 0, vy: 0,
    ...p,
  };
}

export function buildSeed(): GraphState {
  const nodes = new Map<string, Node>();
  const edges = new Map<string, Edge>();

  // --- Brazil cluster ---
  nodes.set("orbid:5493-aves-aurora-br", mkNode({
    id: "orbid:5493-aves-aurora-br",
    kind: "LegalEntity",
    state: "verified",
    accent: "verify",
    label: "Aurora Aves Brasil",
    initials: "AA",
    flag: "BR",
    x: BR_POLE.x - 0.04, y: BR_POLE.y,
  }));
  nodes.set("orbid:5493-pampagro-br", mkNode({
    id: "orbid:5493-pampagro-br",
    kind: "LegalEntity",
    state: "cold",  // dormant in the periphery; ignites in T3
    accent: "cold",
    label: "Pampagro Agronegócios",
    initials: "PA",
    flag: "BR",
    x: BR_POLE.x - 0.18, y: BR_POLE.y + 0.42,
  }));

  // BR-side attestor placeholders
  nodes.set("orbid:cdial-halal-br", mkNode({
    id: "orbid:cdial-halal-br",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "CDIAL Halal",
    initials: "CD",
    flag: "BR",
    x: BR_POLE.x - 0.22, y: BR_POLE.y - 0.30,
  }));
  nodes.set("orbid:abcc-cert-br", mkNode({
    id: "orbid:abcc-cert-br",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "ABCC Cert-H",
    initials: "AB",
    flag: "BR",
    x: BR_POLE.x - 0.38, y: BR_POLE.y - 0.14,
  }));
  nodes.set("orbid:mapa-export-br", mkNode({
    id: "orbid:mapa-export-br",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "MAPA",
    initials: "MA",
    flag: "BR",
    x: BR_POLE.x - 0.32, y: BR_POLE.y + 0.18,
  }));
  nodes.set("orbid:fambras-br", mkNode({
    id: "orbid:fambras-br",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "FAMBRAS",
    initials: "FB",
    flag: "BR",
    x: BR_POLE.x - 0.50, y: BR_POLE.y + 0.05,
  }));

  // --- UAE cluster ---
  nodes.set("orbid:5493-almadar-ae", mkNode({
    id: "orbid:5493-almadar-ae",
    kind: "LegalEntity",
    state: "cold",   // *the* dim cold dot — ignites in Act 1.6
    accent: "cold",
    label: "Al Madar Foods",
    initials: "AM",
    flag: "AE",
    x: AE_POLE.x + 0.10, y: AE_POLE.y - 0.06,
  }));
  nodes.set("orbid:5493-gulfphosphate-ae", mkNode({
    id: "orbid:5493-gulfphosphate-ae",
    kind: "LegalEntity",
    state: "cold",  // dormant; ignites in T3
    accent: "cold",
    label: "Gulf Phosphate UAE",
    initials: "GP",
    flag: "AE",
    x: AE_POLE.x + 0.18, y: AE_POLE.y + 0.42,
  }));

  // AE-side attestor placeholders
  nodes.set("orbid:moccae-ae", mkNode({
    id: "orbid:moccae-ae",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "MOCCAE",
    initials: "MO",
    flag: "AE",
    x: AE_POLE.x + 0.32, y: AE_POLE.y - 0.30,
  }));
  nodes.set("orbid:fed-uae-chambers", mkNode({
    id: "orbid:fed-uae-chambers",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "Federation of UAE Chambers",
    initials: "FUC",
    flag: "AE",
    x: AE_POLE.x + 0.44, y: AE_POLE.y - 0.10,
  }));
  nodes.set("orbid:sgs-cargo-ae", mkNode({
    id: "orbid:sgs-cargo-ae",
    kind: "AttestationPartner",
    state: "cold",
    accent: "cold",
    label: "SGS",
    initials: "SG",
    flag: "AE",
    x: AE_POLE.x + 0.40, y: AE_POLE.y + 0.18,
  }));

  // --- GLEIF cluster (back-centre, ignites on first OrbID mint) ---
  nodes.set("gleif", mkNode({
    id: "gleif",
    kind: "GleifCluster",
    state: "cold",
    accent: "cold",
    label: "GLEIF",
    initials: "GLEIF",
    x: 0.00, y: -0.55,
  }));

  // --- Aurora's prior history: thin counterparty edges to her certifiers ---
  edges.set("aurora->cdial", {
    id: "aurora->cdial",
    kind: "attestation",
    from: "orbid:5493-aves-aurora-br",
    to: "orbid:cdial-halal-br",
    state: "verified",
    weight: 0.22,
    hcsAnchors: [],
  });
  edges.set("aurora->abcc", {
    id: "aurora->abcc",
    kind: "attestation",
    from: "orbid:5493-aves-aurora-br",
    to: "orbid:abcc-cert-br",
    state: "verified",
    weight: 0.18,
    hcsAnchors: [],
  });
  edges.set("aurora->mapa", {
    id: "aurora->mapa",
    kind: "attestation",
    from: "orbid:5493-aves-aurora-br",
    to: "orbid:mapa-export-br",
    state: "verified",
    weight: 0.18,
    hcsAnchors: [],
  });

  return {
    nodes,
    edges,
    fullBleed: false,
    updatedAt: Date.now(),
  };
}
